import { config } from "./config";

interface Tokens {
  idToken: string;
  refreshToken: string;
  expiresAt: number;
}

const STORAGE_KEY = "sc.staff.session";
const listeners = new Set<() => void>();

export class AuthError extends Error {}

export type SignInResult =
  | { kind: "signed_in" }
  | { kind: "new_password"; session: string; username: string };

function load(): Tokens | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Tokens) : null;
  } catch {
    return null;
  }
}

function save(tokens: Tokens | null) {
  if (tokens) localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
  else localStorage.removeItem(STORAGE_KEY);
  listeners.forEach((l) => l());
}

export function onAuthChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function isSignedIn(): boolean {
  return load() !== null;
}

async function cognito<T>(target: string, body: Record<string, unknown>): Promise<T> {
  const res = await fetch(`https://cognito-idp.${config.region}.amazonaws.com/`, {
    method: "POST",
    headers: {
      "content-type": "application/x-amz-json-1.1",
      "x-amz-target": `AWSCognitoIdentityProviderService.${target}`,
    },
    body: JSON.stringify({ ClientId: config.cognitoClientId, ...body }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const type = String(data.__type ?? "");
    if (type.includes("NotAuthorized")) throw new AuthError("Incorrect email or password.");
    if (type.includes("UserNotFound")) throw new AuthError("Incorrect email or password.");
    if (type.includes("InvalidPassword")) throw new AuthError(data.message ?? "That password doesn't meet the requirements.");
    if (type.includes("CodeMismatch")) throw new AuthError("That code didn't match.");
    if (type.includes("ExpiredCode")) throw new AuthError("That code expired. Request a new one.");
    if (type.includes("LimitExceeded") || type.includes("TooManyRequests")) throw new AuthError("Too many attempts. Try again in a few minutes.");
    throw new AuthError(data.message ?? "Sign-in failed. Please try again.");
  }
  return data as T;
}

interface AuthResult {
  AuthenticationResult?: { IdToken: string; RefreshToken?: string; ExpiresIn: number };
  ChallengeName?: string;
  Session?: string;
}

function store(result: NonNullable<AuthResult["AuthenticationResult"]>, refreshToken?: string) {
  save({
    idToken: result.IdToken,
    refreshToken: result.RefreshToken ?? refreshToken ?? "",
    expiresAt: Date.now() + (result.ExpiresIn - 60) * 1000,
  });
}

export async function signIn(email: string, password: string): Promise<SignInResult> {
  const r = await cognito<AuthResult>("InitiateAuth", {
    AuthFlow: "USER_PASSWORD_AUTH",
    AuthParameters: { USERNAME: email.trim(), PASSWORD: password },
  });
  if (r.ChallengeName === "NEW_PASSWORD_REQUIRED" && r.Session) {
    return { kind: "new_password", session: r.Session, username: email.trim() };
  }
  if (!r.AuthenticationResult) throw new AuthError("Sign-in needs a step this app doesn't support yet.");
  store(r.AuthenticationResult);
  return { kind: "signed_in" };
}

export async function completeNewPassword(username: string, session: string, newPassword: string): Promise<void> {
  const r = await cognito<AuthResult>("RespondToAuthChallenge", {
    ChallengeName: "NEW_PASSWORD_REQUIRED",
    Session: session,
    ChallengeResponses: { USERNAME: username, NEW_PASSWORD: newPassword },
  });
  if (!r.AuthenticationResult) throw new AuthError("Couldn't set the new password.");
  store(r.AuthenticationResult);
}

export async function forgotPassword(email: string): Promise<void> {
  await cognito("ForgotPassword", { Username: email.trim() });
}

export async function confirmForgotPassword(email: string, code: string, password: string): Promise<void> {
  await cognito("ConfirmForgotPassword", { Username: email.trim(), ConfirmationCode: code.trim(), Password: password });
}

export function signOut(): void {
  save(null);
}

let refreshing: Promise<string | null> | null = null;

/** Returns a valid ID token, refreshing it when it's about to expire. */
export async function idToken(): Promise<string | null> {
  const tokens = load();
  if (!tokens) return null;
  if (Date.now() < tokens.expiresAt) return tokens.idToken;

  refreshing ??= cognito<AuthResult>("InitiateAuth", {
    AuthFlow: "REFRESH_TOKEN_AUTH",
    AuthParameters: { REFRESH_TOKEN: tokens.refreshToken },
  })
    .then((r) => {
      if (!r.AuthenticationResult) throw new AuthError("Session expired");
      store(r.AuthenticationResult, tokens.refreshToken);
      return r.AuthenticationResult.IdToken;
    })
    .catch(() => {
      save(null);
      return null;
    })
    .finally(() => {
      refreshing = null;
    });
  return refreshing;
}
